import { useEffect, useState } from 'react';

import { copy } from '../../core/copy';
import { shortDate, weekOfMembership } from '../../core/time';
import { countRevisions, countWorks } from '../../core/rules';
import { Win } from '../components';
import { api } from '../api';
import { contentOf, playerOf, useStore } from '../store';

interface Entry {
  kind: string;
  nodeId: string | null;
  islandId: string | null;
  at: number;
  payload: Record<string, unknown>;
}

/**
 * The journal writes itself.
 *
 * Nobody is going to keep a learning diary, so this is assembled from what already happened. Every
 * posted work with a link is one row of a portfolio the student walks away with — which is the
 * answer to the sharpest criticism of gamification: make the loop produce something that still has
 * value after the game is switched off.
 */
export function Dnevnik() {
  const content = useStore(contentOf);
  const player = useStore(playerOf);
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .journal()
      .then((result) => {
        if (!cancelled) setEntries(result.entries);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const works = countWorks(content, player);
  const revisions = countRevisions(content, player);

  const titleOf = (entry: Entry): string => {
    const fromPayload = typeof entry.payload.title === 'string' ? entry.payload.title : null;
    if (fromPayload) return fromPayload;
    for (const island of content.islands) {
      const node = island.nodes.find((n) => n.id === entry.nodeId);
      if (node) return node.title;
    }
    return entry.nodeId ?? '';
  };

  const islandName = (id: string | null): string =>
    content.islands.find((i) => i.id === id)?.name ?? id ?? '';

  const describe = (entry: Entry): { text: string; tone: string; url?: string } | null => {
    const url = typeof entry.payload.url === 'string' ? entry.payload.url : undefined;
    switch (entry.kind) {
      case 'node.post':
        return { text: copy.journal.posted(titleOf(entry)), tone: '', url };
      case 'node.revise':
        return { text: copy.journal.revisedEntry(titleOf(entry)), tone: 'gold', url };
      case 'node.done':
        return { text: copy.journal.finished(titleOf(entry)), tone: 'green' };
      case 'island.cleared':
        return { text: copy.journal.islandEntry(islandName(entry.islandId)), tone: 'gold' };
      default:
        return null;
    }
  };

  return (
    <div className="pad">
      <Win title={copy.journal.title}>
        <p className="muted">{copy.journal.lead}</p>
        <div className="btn-row" style={{ marginTop: 12 }}>
          <span className="chip">{copy.stats.line(works, revisions)}</span>
        </div>
      </Win>

      <div style={{ marginTop: 22 }}>
        {failed ? (
          <p className="muted">{copy.error.genericBody}</p>
        ) : entries === null ? (
          <p className="muted">…</p>
        ) : entries.length === 0 ? (
          <p className="muted" style={{ color: '#9dc0d1' }}>
            {copy.journal.empty}
          </p>
        ) : (
          entries.map((entry, index) => {
            const described = describe(entry);
            if (!described) return null;
            const week = weekOfMembership(dayOf(entry, player.day));
            return (
              <div className="jrow" key={`${entry.kind}-${entry.at}-${index}`}>
                <span className={`dot ${described.tone}`.trim()} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="tiny muted" style={{ letterSpacing: '0.1em' }}>
                    {copy.journal.week(week)} · {shortDate(entry.at)}
                  </div>
                  <div style={{ color: '#f5fafc', fontSize: 14 }}>{described.text}</div>
                  {described.url ? (
                    <a
                      className="tiny"
                      href={described.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      style={{ color: '#48c4e5' }}
                    >
                      {copy.journal.work}
                    </a>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/** Membership week for an entry, derived from the day counter the server already computed. */
function dayOf(entry: Entry, currentDay: number): number {
  const daysAgo = Math.max(0, Math.round((Date.now() - entry.at) / 86_400_000));
  return Math.max(0, currentDay - daysAgo);
}
